<template>
	<div class="flex flex-col gap-12">
		<section
			v-for="cookieConsent in cookieConsents"
			:key="`organism-form-cookie-consent-${cookieConsent.key}`"
			class="flex flex-col border rounded border-gray-200 bg-black/5"
		>
			<div class="flex justify-between items-center p-8">
				<div class="flex flex-col gap-4 pr-12">
					<LazyAtomTitle
						:id="`cookie-consent-${cookieConsent.key}`"
						tag="h2"
						size="sm"
					>
						{{ cookieConsent.label }}
					</LazyAtomTitle>
					<p
						:id="`cookie-consent-description-${cookieConsent.key}`"
						class="text-paragraph-size-sm leading-normal mb-0"
					>
						{{ cookieConsent.description }}
					</p>
					<div>
						<AtomButton
							v-if="
								cookieConsent.subCookieConsents
									&& cookieConsent.subCookieConsents.length > 0
							"
							:icon-left="
								subConsentsOpened.includes(cookieConsent.key)
									? 'fa-xmark'
									: 'fa-gears'
							"
							type="button"
							class="text-left hover:underline text-functional-size-sm"
							:text="
								subConsentsOpened.includes(cookieConsent.key)
									? 'Geavanceerde instellingen sluiten'
									: 'Geavanceerde instellingen'
							"
							:variant="ButtonVariant.Transparent"
							@click="toggleSubConsents(cookieConsent.key)"
						/>
					</div>
				</div>
				<LazyAtomSwitch
					v-if="!cookieConsent.required"
					class="bg-purple-800 text-white"
					:aria-labelledby="`cookie-consent-${cookieConsent.key}`"
					:aria-describedby="`cookie-consent-description-${cookieConsent.key}`"
					:options="switchOptions"
					:value="
						checkConsent(cookieConsent.key)
							? switchOptions[0]
							: switchOptions[1]
					"
					size="sm"
					@change="
						(v) => toggleGroupSwitch(cookieConsent.key, v === 'on')
					"
				/>
			</div>
			<div
				v-if="subConsentsOpened.includes(cookieConsent.key)"
				class="flex flex-col gap-8 pl-6 md:pl-12 p-8 border-t border-gray-200"
			>
				<div
					v-for="subCookieConsent in cookieConsent.subCookieConsents"
					:key="`organism-form-cookie-consent-${cookieConsent.key}-${subCookieConsent.key}`"
					class="flex justify-between items-center"
				>
					<div class="flex flex-col gap-4 pr-12">
						<LazyAtomTitle
							:aria-labelledby="`cookie-consent-${cookieConsent.key}`"
							tag="h2"
							size="sm"
						>
							{{ subCookieConsent.label }}
						</LazyAtomTitle>
						<p
							:id="`cookie-consent-description-${cookieConsent.key}`"
							class="text-paragraph-size-sm leading-normal"
						>
							{{ subCookieConsent.description }}
						</p>
					</div>
					<LazyAtomSwitch
						v-if="!subCookieConsent.required"
						:aria-labelledby="`cookie-consent-${cookieConsent.key}`"
						:aria-describedby="`cookie-consent-description-${cookieConsent.key}`"
						class="bg-purple-800 text-white"
						:options="switchOptions"
						:value="
							checkConsent(subCookieConsent.key)
								? switchOptions[0]
								: switchOptions[1]
						"
						size="sm"
						@change="
							(v) =>
								togglePlatformSwitch(
									cookieConsent.key,
									subCookieConsent.key,
									v === 'on',
								)
						"
					/>
				</div>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import { cookieConsents } from '#base/core/consts/cookieConsents';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';
import type { GtagConsent } from '#base/core/enums/GtagConsentEnum';
import type { CookieConsentCombinedType } from '#base/core/types/CookieConsentType';

const {
	cookieSettings,
} = useConsent();

const subConsentsOpened = reactive<CookieConsentCombinedType[]>([]);

const switchOptions = [
	{
		label: 'Aan',
		value: 'on',
	},
	{
		label: 'Uit',
		value: 'off',
	},
];

const checkConsent = (consent: CookieConsentCombinedType) => {
	return cookieSettings.value[consent] === 'on';
};
const toggleGroupSwitch = (
	consent: CookieConsentCombinedType,
	isOn: boolean
) => {
	cookieSettings.value[consent] = isOn ? 'on' : 'off';

	const consentSettings = cookieConsents.find(c => c.key === consent);
	if (consentSettings && consentSettings.subCookieConsents) {
		for (const subConsent of consentSettings.subCookieConsents) {
			cookieSettings.value[subConsent.key] = isOn ? 'on' : 'off';
		}
	}
};

const togglePlatformSwitch = (
	groupConsent: CookieConsentCombinedType,
	gtagConsent: GtagConsent,
	isOn: boolean
) => {
	if (isOn) {
		cookieSettings.value[groupConsent] = 'on';
		cookieSettings.value[gtagConsent] = 'on';
	}
	else {
		cookieSettings.value[gtagConsent] = 'off';

		const consentSettings = cookieConsents.find(
			c => c.key === groupConsent
		);

		if (consentSettings && consentSettings.subCookieConsents) {
			let isEachPlatformTurnedOff = true;
			for (const subConsent of consentSettings.subCookieConsents) {
				if (cookieSettings.value[subConsent.key] === 'on') {
					isEachPlatformTurnedOff = false;
				}
			}

			if (isEachPlatformTurnedOff) {
				cookieSettings.value[groupConsent] = 'off';
			}
		}
	}
};

const toggleSubConsents = (consent: CookieConsentCombinedType) => {
	if (subConsentsOpened.includes(consent)) {
		const index = subConsentsOpened.indexOf(consent);
		subConsentsOpened.splice(index, 1);
	}
	else {
		subConsentsOpened.push(consent);
	}
};
</script>
